import React, { useState, useEffect } from 'react';
import { CreditCard, Smartphone, QrCode, CheckCircle, Clock, ArrowLeft, Copy, Check, Upload, Tag } from 'lucide-react';
import PayUPayment from './Payupayment';

export default function PaymentPage() {
  const [paymentMethod, setPaymentMethod] = useState('upi');
  const [bookingData, setBookingData] = useState(null);
  const [paymentData, setPaymentData] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState('pending');
  const [copied, setCopied] = useState(false);
  const [timeLeft, setTimeLeft] = useState(600);
  const [screenshot, setScreenshot] = useState(null);
  const [transactionId, setTransactionId] = useState('');
  const [creatingPayment, setCreatingPayment] = useState(false);
  
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [authChecking, setAuthChecking] = useState(true); // ✅ NEW: Track auth check status

  // ✅ FIXED: Check authentication FIRST before allowing access
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const userData = localStorage.getItem('userData');

    if (token && userData) {
      try {
        setUser(JSON.parse(userData));
        setIsAuthenticated(true);
        setAuthChecking(false);
        console.log('✅ User authenticated:', JSON.parse(userData));
      } catch (error) {
        console.error('❌ Error parsing user data:', error);
        // Clear invalid data
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
        // Redirect to login
        alert('Session expired. Please login again.');
        window.location.href = '/login';
      }
    } else {
      // No authentication found - redirect to login
      console.warn('⚠️ No authentication found, redirecting to login...');
      alert('Please login to access payment page.');
      window.location.href = '/login';
    }
  }, []);

  useEffect(() => {
    // ✅ FIXED: Only load booking data if user is authenticated
    if (!isAuthenticated) return;

    const booking = sessionStorage.getItem('currentBooking');
    const payment = sessionStorage.getItem('currentPayment');

    if (booking) {
      const parsedBooking = JSON.parse(booking);
      console.log('📋 Booking Data:', parsedBooking);
      console.log('🎟️ Discount:', parsedBooking.discount);
      console.log('💰 Original Price:', parsedBooking.originalPrice);
      console.log('💳 Total Price:', parsedBooking.totalPrice);
      setBookingData(parsedBooking);
    } else {
      if (paymentStatus !== 'success') {
        console.warn('⚠️ No booking data found, redirecting to dashboard...');
        alert('No booking found. Please create a booking first.');
        setTimeout(() => {
          window.location.href = '/dashboard';
        }, 2000);
      }
    }

    if (payment) {
      setPaymentData(JSON.parse(payment));
    }
  }, [paymentStatus, isAuthenticated]);

  useEffect(() => {
    const createPaymentRecord = async () => {
      if (bookingData && !paymentData && paymentMethod === 'card' && !creatingPayment) {
        setCreatingPayment(true);
        try {
          console.log('💳 Creating payment record for card payment...');
          
          const response = await fetch(
            'https://api.boomontrip.com/api/payments/create',
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                bookingId: bookingData._id,
                amount: bookingData.totalPrice,
                paymentMethod: 'card'
              })
            }
          );

          const data = await response.json();
          
          if (data.success) {
            console.log('✅ Payment record created:', data.data);
            setPaymentData(data.data);
            sessionStorage.setItem('currentPayment', JSON.stringify(data.data));
          } else {
            console.error('❌ Failed to create payment record:', data.message);
          }
        } catch (error) {
          console.error('❌ Error creating payment record:', error);
        } finally {
          setCreatingPayment(false);
        }
      }
    };

    createPaymentRecord();
  }, [bookingData, paymentData, paymentMethod, creatingPayment]);

  useEffect(() => {
    if (paymentStatus === 'pending' && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [timeLeft, paymentStatus]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const upiId = 'boomontrip@pingpay';

  const upiLink = bookingData ?
    `upi://pay?pa=${upiId}&pn=BoomOnTrip&am=${bookingData.totalPrice}&cu=INR&tn=BookingRef${bookingData.bookingReference}`
    : '';

  const qrCodeUrl = bookingData ?
    `https://chart.googleapis.com/chart?chs=300x300&cht=qr&chl=${encodeURIComponent(upiLink)}&choe=UTF-8`
    : '';

  const handleCopyUPI = () => {
    navigator.clipboard.writeText(upiId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleScreenshotUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setScreenshot(file);
    }
  };

  const handlePaymentConfirm = async () => {
    if (!transactionId.trim()) {
      alert('Please enter your transaction ID');
      return;
    }

    if (!paymentData || !paymentData._id) {
      alert('Payment session expired. Please start again.');
      window.location.href = '/dashboard';
      return;
    }

    setPaymentStatus('processing');

    try {
      console.log('🔄 Confirming payment...', {
        paymentId: paymentData._id,
        transactionRef: transactionId
      });

      const response = await fetch('https://api.boomontrip.com/api/payments/confirm', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          paymentId: paymentData._id,
          transactionRef: transactionId,
          upiId: paymentMethod === 'upi' ? upiId : undefined,
          paymentMethod: paymentMethod
        })
      });

      console.log('📡 Response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Payment confirmation failed');
      }

      const data = await response.json();
      console.log('✅ Payment response:', data);

      if (data.success) {
        setPaymentStatus('success');
      } else {
        throw new Error(data.message || 'Payment confirmation failed');
      }
    } catch (error) {
      console.error('❌ Error confirming payment:', error);
      alert('Error: ' + error.message);
      setPaymentStatus('pending');
    }
  };

  const handleBackClick = () => {
    // ✅ FIXED: Always go to dashboard for authenticated users
    window.location.href = '/dashboard';
  };

  // ✅ NEW: Show loading while checking authentication
  if (authChecking) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-red-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Verifying authentication...</p>
        </div>
      </div>
    );
  }

  // Loading state
  if (!bookingData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-red-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading booking details...</p>
        </div>
      </div>
    );
  }

  // Success state
  if (paymentStatus === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>
          <h2 className="text-3xl font-bold text-gray-800 mb-2">Payment Under Verification!</h2>
          <p className="text-gray-600 mb-6">Your payment details have been submitted</p>

          <div className="bg-gray-50 rounded-2xl p-6 mb-6">
            <div className="space-y-3 text-left">
              <div className="flex justify-between">
                <span className="text-gray-600">Booking Reference:</span>
                <span className="font-bold text-orange-600">{bookingData.bookingReference}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Amount Paid:</span>
                <span className="font-bold">₹{bookingData.totalPrice.toLocaleString('en-IN')}</span>
              </div>
              {bookingData.discount && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Discount Applied:</span>
                  <span className="font-bold text-green-600">{bookingData.discount.percent}% OFF</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-600">Tour Date:</span>
                <span className="font-semibold">{new Date(bookingData.date).toLocaleDateString('en-IN')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Transaction ID:</span>
                <span className="font-semibold text-sm">{transactionId}</span>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-yellow-800">
              ⏱️ Our team will verify your payment within 2-4 hours and send confirmation to {bookingData.email}
            </p>
          </div>

          {/* ✅ Always show Dashboard button for authenticated users */}
          <button
            onClick={() => window.location.href = '/dashboard'}
            className="w-full bg-gradient-to-r from-orange-600 to-red-600 text-white py-4 rounded-xl font-bold text-lg hover:shadow-lg transition-all"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Main payment page
  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-red-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* ✅ FIXED: Back button always goes to dashboard */}
        <button
          onClick={handleBackClick}
          className="flex items-center text-gray-700 hover:text-orange-600 mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Dashboard
        </button>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Booking Summary */}
          <div className="md:col-span-1">
            <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Booking Summary</h3>

              <div className="space-y-3 text-sm">
                <div>
                  <p className="text-gray-600">Reference</p>
                  <p className="font-semibold text-orange-600">{bookingData.bookingReference}</p>
                </div>

                <div>
                  <p className="text-gray-600">Name</p>
                  <p className="font-semibold">{bookingData.name}</p>
                </div>

                <div>
                  <p className="text-gray-600">Tour Date</p>
                  <p className="font-semibold">{new Date(bookingData.date).toLocaleDateString('en-IN')}</p>
                </div>

                <div>
                  <p className="text-gray-600">Pickup</p>
                  <p className="font-semibold text-xs">{bookingData.pickup}</p>
                </div>

                <div className="pt-3 border-t">
                  <p className="text-gray-600">Travelers</p>
                  <p className="font-semibold">{bookingData.adults} Adults, </p>
                </div>

                {/* Discount Section */}
                {bookingData.discount && bookingData.originalPrice && (
                  <div className="pt-3 border-t">
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Original Price</span>
                        <span className="text-gray-400 line-through">₹{bookingData.originalPrice.toLocaleString('en-IN')}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-green-600 font-semibold flex items-center gap-1">
                          <Tag className="w-3 h-3" />
                          {bookingData.discount.type === 'couple' ? 'Couple Discount' :
                            bookingData.discount.type === 'family_first' ? 'Family First' :
                              'First Booking'} ({bookingData.discount.percent}%)
                        </span>
                        <span className="text-green-600 font-semibold">-₹{Math.round(bookingData.discount.amount).toLocaleString('en-IN')}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-6 pt-4 border-t">
                <div className="flex justify-between items-center">
                  <span className="text-gray-700 font-semibold">Total Amount</span>
                  <div className="text-right">
                    {bookingData.discount && (
                      <div className="text-sm text-gray-400 line-through">
                        ₹{bookingData.originalPrice?.toLocaleString('en-IN')}
                      </div>
                    )}
                    <span className="text-2xl font-bold text-orange-600">
                      ₹{bookingData.totalPrice.toLocaleString('en-IN')}
                    </span>
                  </div>
                </div>
              </div>

              <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <div className="flex items-center text-yellow-800">
                  <Clock className="w-4 h-4 mr-2" />
                  <span className="text-sm font-semibold">Time Left: {formatTime(timeLeft)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Section */}
          <div className="md:col-span-2">
            <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Complete Payment</h2>

              {/* Payment Method Selection */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                <button
                  onClick={() => setPaymentMethod('card')}
                  className={`p-4 rounded-xl border-2 transition-all ${paymentMethod === 'card'
                      ? 'border-orange-600 bg-orange-50'
                      : 'border-gray-200 hover:border-orange-300'
                    }`}
                >
                  <CreditCard className={`w-8 h-8 mx-auto mb-2 ${paymentMethod === 'card' ? 'text-orange-600' : 'text-gray-400'}`} />
                  <p className="font-semibold text-sm">Proceed to the Payment </p>
                </button>
              </div>

              {/* Card Payment */}
              {paymentMethod === 'card' && bookingData && (
                <>
                  {creatingPayment ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
                      <p className="text-gray-600">Preparing payment gateway...</p>
                    </div>
                  ) : paymentData ? (
                    <PayUPayment 
                      bookingId={paymentData.bookingId}
                      bookingData={bookingData}
                    />
                  ) : (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <p className="text-sm text-yellow-800">
                        Unable to initialize card payment. Please try again or contact support.
                      </p>
                    </div>
                  )}
                </>
              )}

              <div className="mt-6 text-center">
                <p className="text-xs text-gray-500">
                  Your payment information is secure and will be verified by our team
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}